import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useProject } from './ProjectContext';
import api from '../api';

interface TerminologyData {
  term: string;
  definition: string;
  is_acronym: boolean;
  confidence_score: number;
}

interface FullTerminologyData {
  summary: {
    totalTerms: number;
    knownTerms: number;
    unknownAcronyms: number;
    complexityScore: number;
    internalTerms: number;
    externalTerms: number;
  };
  enhancedTerms: any[];
  knownTerms: any[];
  unknownAcronyms: string[];
  aiResolvedAcronyms: any[];
  internalTerms: any[];
  categories: Set<string>;
}

interface TerminologyContextType {
  terminologyMap: Map<string, TerminologyData>;
  fullTerminologyData: FullTerminologyData | null;
  loading: boolean;
  analysisLoading: boolean;
  refreshTerminology: () => Promise<void>;
  loadFullTerminology: () => Promise<void>;
  setFullTerminologyData: (data: FullTerminologyData | null) => void;
  getTermDefinition: (term: string) => TerminologyData | undefined;
}

const TerminologyContext = createContext<TerminologyContextType | undefined>(undefined);

interface TerminologyProviderProps {
  children: ReactNode;
}

export const TerminologyProvider: React.FC<TerminologyProviderProps> = ({ children }) => {
  const [terminologyMap, setTerminologyMap] = useState<Map<string, TerminologyData>>(new Map());
  const [fullTerminologyData, setFullTerminologyDataState] = useState<FullTerminologyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const { currentProject } = useProject();

  const refreshTerminology = useCallback(async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    try {
      const response = await api.get(`/api/terminology/${currentProject.id}/map`);
      const terminologyData = response.data.data.terminology_map;
      
      // Convert object back to Map
      const newMap = new Map<string, TerminologyData>();
      Object.entries(terminologyData).forEach(([key, value]) => {
        newMap.set(key, value as TerminologyData);
      });
      
      setTerminologyMap(newMap);
    } catch (error) {
      console.error('Error loading terminology map:', error);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id]);

  const loadFullTerminology = useCallback(async () => {
    if (!currentProject?.id) return;

    setAnalysisLoading(true);
    try {
      const response = await api.get(`/api/terminology/${currentProject.id}`);
      const terms = response.data.data.terms;

      const combinedTerminology: FullTerminologyData = {
        summary: {
          totalTerms: terms.length,
          knownTerms: terms.filter((t: any) => t.is_approved || t.manual_approval_status === 'approved').length,
          unknownAcronyms: terms.filter((t: any) => t.requires_review).length,
          complexityScore: Math.round(terms.reduce((avg: number, t: any) => avg + t.confidence_score, 0) / Math.max(terms.length, 1)),
          internalTerms: terms.filter((t: any) => t.source_documents?.length === 1).length,
          externalTerms: terms.filter((t: any) => t.source_documents?.length > 1).length
        },
        enhancedTerms: terms,
        knownTerms: terms.filter((t: any) => t.is_approved || t.manual_approval_status === 'approved'),
        unknownAcronyms: terms.filter((t: any) => t.requires_review && t.is_acronym).map((t: any) => t.term),
        aiResolvedAcronyms: terms.filter((t: any) => !t.requires_review && t.is_acronym),
        internalTerms: terms.filter((t: any) => t.source_documents?.length === 1),
        categories: new Set<string>(terms.map((t: any) => t.complexity_level))
      };

      setFullTerminologyDataState(combinedTerminology);
    } catch (error) {
      console.error('Error loading full terminology:', error);
      setFullTerminologyDataState(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [currentProject?.id]);

  const setFullTerminologyData = useCallback((data: FullTerminologyData | null) => {
    setFullTerminologyDataState(data);
  }, []);

  const getTermDefinition = (term: string): TerminologyData | undefined => {
    return terminologyMap.get(term.toLowerCase());
  };

  // Load terminology when project changes
  useEffect(() => {
    if (currentProject?.id) {
      refreshTerminology();
      loadFullTerminology();
    } else {
      // Clear data when no project
      setFullTerminologyDataState(null);
    }
  }, [currentProject?.id, refreshTerminology, loadFullTerminology]);

  const value: TerminologyContextType = {
    terminologyMap,
    fullTerminologyData,
    loading,
    analysisLoading,
    refreshTerminology,
    loadFullTerminology,
    setFullTerminologyData,
    getTermDefinition
  };

  return (
    <TerminologyContext.Provider value={value}>
      {children}
    </TerminologyContext.Provider>
  );
};

export const useTerminology = (): TerminologyContextType => {
  const context = useContext(TerminologyContext);
  if (!context) {
    throw new Error('useTerminology must be used within a TerminologyProvider');
  }
  return context;
};